import * as Sentry from '@sentry/react';
import { Form } from '@superfunnel/types';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import Input from '@superfunnel/ui-core/v2/Input';
import { capitalize } from '@superfunnel/utils';
import { ColumnDef } from '@tanstack/react-table';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import {
  Database,
  EditPencil,
  List,
  MoreVert,
  MultiplePagesEmpty,
  Plus,
  Search,
  Trash,
} from 'iconoir-react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { CustomDataGrid } from '../components/CustomDataGrid/CustomDataGrid';
import { AuthContext } from '../contexts/AuthContext';
import { DashboardContext } from '../contexts/DashboardContext';
import { SnackbarContext } from '../contexts/SnackbarContext';
import { UserContext } from '../contexts/UserContext';
import CreateFormModal from '../modals/CreateFormModal';
import EditStepperFormModal from '../modals/EditStepperFormModal/EditStepperFormModal';
import { deleteForm, duplicateForm } from '../services/FormsService';
import { track } from '../utils/analytics';

export default function Forms() {
  const { show } = useContext(SnackbarContext);
  const { firebaseUser } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const { forms } = useContext(DashboardContext);
  const [createSimpleFormDialogOpen, setCreateSimpleFormDialogOpen] =
    useState(false);
  const [createStepperFormDialogOpen, setCreateStepperFormDialogOpen] =
    useState(false);
  const [editingForm, setEditingForm] = useState<{
    form: Form;
    id: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter out htmlStepper forms (those are shown in Quiz Funnels)
  const simpleAndStepperForms = useMemo(() => {
    return forms?.filter((form) => form.data().formType !== 'htmlStepper');
  }, [forms]);

  // Apply search filter
  const filteredForms = useMemo(() => {
    if (!simpleAndStepperForms) return undefined;
    if (!searchQuery.trim()) return simpleAndStepperForms;

    const query = searchQuery.toLowerCase().trim();
    return simpleAndStepperForms.filter((formDoc) => {
      const form = formDoc.data();
      // Match name (contains) or ID (exact match)
      const nameMatches = form.name.toLowerCase().includes(query);
      const idMatches = formDoc.id.toLowerCase() === query;
      return nameMatches || idMatches;
    });
  }, [simpleAndStepperForms, searchQuery]);

  const onDeleteFormClick = useCallback(
    (formId: string) => {
      if (!selectedAccount) return;

      deleteForm(selectedAccount.id, formId)
        .then(() => {
          show('Deleted!');
        })
        .catch((e) => {
          show('Failed deleting form, ' + e.message || e.toString());
          Sentry.captureException(e);
        });
    },
    [selectedAccount, show],
  );

  const onDuplicateFormClick = useCallback(
    (form: Form) => {
      if (!selectedAccount) return;

      duplicateForm(selectedAccount.id, form, firebaseUser?.uid ?? '')
        .then(() => {
          show('Duplicated form');
        })
        .catch((e) => {
          show('Failed duplicating form, ' + e.message || e.toString());
          Sentry.captureException(e);
        });
    },
    [selectedAccount, firebaseUser?.uid, show],
  );

  const columns: ColumnDef<QueryDocumentSnapshot<Form, Form>>[] =
    useMemo(() => {
      return [
        {
          id: 'name',
          header: 'Name',
          accessorFn: (row) => row.data().name,
          size: 350,
          cell: ({ row: { original } }) => {
            return (
              <div className="flex flex-col">
                <div className="text-gray-900">{original.data().name}</div>
                <div className="text-gray-500">{original.id}</div>
              </div>
            );
          },
        },
        {
          id: 'formType',
          header: 'Type',
          accessorFn: (row) => {
            return row.data().formType ?? 'simple';
          },
          cell: ({ row: { original } }) => {
            const form = original.data();
            if (form.formType === 'stepper') {
              return 'Stepper';
            }
            return 'Form';
          },
          size: 120,
        },
        {
          id: 'formStatus',
          header: 'Status',
          size: 100,
          accessorFn: (row) => {
            return row.data().status ?? 'active';
          },
          cell: ({ row: { original } }) => {
            return (
              <Badge
                variant={
                  original.data().status === 'active' ? 'success' : 'default'
                }
                className="text-xs"
              >
                {capitalize(original.data().status ?? 'active')}
              </Badge>
            );
          },
        },
        {
          id: 'fields',
          header: 'Number of fields',
          accessorFn: (row) => {
            const form = row.data();
            if (form.formType === 'stepper') {
              return form.steps.reduce((acc, step) => {
                return acc + ('fields' in step ? step.fields.length : 0);
              }, 0);
            }
            // Simple forms have fields directly
            if ('fields' in form) {
              return form.fields.length;
            }
            return 0;
          },
          size: 250,
        },
        {
          id: 'createdAt',
          header: 'Created At',
          accessorFn: (row) => row.data().createdAt.toDate(),
          size: 200,
          cell: (info) => {
            return info.row.original.data().createdAt.toDate().toLocaleString();
          },
        },
        {
          header: '',
          accessorKey: 'actions',
          size: 100,
          cell: ({ row: { original } }) => (
            <div className="flex items-center gap-2">
              <Button
                size={'icon'}
                type={'button'}
                variant={'outline'}
                className={'rounded-2lg'}
                onClick={() => {
                  track('edit_form_button_click', {
                    formId: original.id,
                  });
                  setEditingForm({
                    form: original.data(),
                    id: original.id,
                  });
                  if (original.data().formType === 'stepper') {
                    setCreateStepperFormDialogOpen(true);
                  } else {
                    setCreateSimpleFormDialogOpen(true);
                  }
                }}
              >
                <EditPencil className="h-4 w-4" />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size={'icon'}
                    type={'button'}
                    variant={'outline'}
                    className={'rounded-2lg'}
                  >
                    <MoreVert
                      strokeWidth={3}
                      className="h-4 w-4"
                      width={20}
                      height={20}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side={'bottom'} align={'end'}>
                  <DropdownMenuItem
                    onClick={() => onDuplicateFormClick(original.data())}
                  >
                    <MultiplePagesEmpty className="h-4 w-4" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant={'destructive'}
                    onClick={() => onDeleteFormClick(original.id)}
                  >
                    <Trash className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ),
        },
      ] satisfies ColumnDef<QueryDocumentSnapshot<Form, Form>>[];
    }, [onDeleteFormClick, onDuplicateFormClick]);

  return (
    <>
      <CreateFormModal
        form={
          editingForm?.form.formType !== 'stepper' &&
          editingForm?.form.formType !== 'htmlStepper'
            ? editingForm?.form
            : undefined
        }
        formId={editingForm?.id}
        open={createSimpleFormDialogOpen}
        onClose={() => {
          setCreateSimpleFormDialogOpen(false);
          setEditingForm(null);
        }}
      />
      <EditStepperFormModal
        form={
          editingForm?.form.formType === 'stepper'
            ? editingForm?.form
            : undefined
        }
        formId={editingForm?.id}
        open={createStepperFormDialogOpen}
        onClose={() => {
          setCreateStepperFormDialogOpen(false);
          setEditingForm(null);
        }}
      />
      <div className="flex h-full flex-col">
        <div className="pb-8 sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base leading-6 font-semibold text-gray-900">
              Forms
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Here you can manage your forms and create new ones.
            </p>
          </div>

          <div className="mt-4 flex flex-row gap-2.5 sm:mt-0 sm:ml-16 sm:flex-none">
            <Input
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type={'button'} className={'rounded-2lg'}>
                  <Plus className="h-4.5 w-4.5 text-white" />
                  New
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side={'bottom'} align={'end'}>
                <DropdownMenuItem
                  onClick={() => {
                    track('create_simple_form_button_click');
                    setCreateSimpleFormDialogOpen(true);
                  }}
                >
                  <List className="h-4 w-4" />
                  New Simple Form
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    track('create_stepper_form_button_click');
                    setCreateStepperFormDialogOpen(true);
                  }}
                >
                  <Database className="h-4 w-4" />
                  New Stepper
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="relative flex-1">
          <CustomDataGrid
            logicalName="Forms"
            columns={columns}
            data={filteredForms}
            initialState={{
              columnPinning: {
                right: ['actions'],
              },
            }}
          />
        </div>
      </div>
    </>
  );
}
